import React, { useEffect, useState } from "react";

import { themevals } from "theme/themevals";
import { getProposalsTable, attachProposalToLead } from "api/Proposals";

/**
 * "Attach an existing proposal" on the lead record's Proposals tab (s.6).
 * Lists proposals attached to nothing, name matches first; attaching moves
 * every version of the proposal onto this lead.
 */
export default function AttachProposalPicker({ leadId, leadName, onAttached }: { leadId: string; leadName: string; onAttached: () => void }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      getProposalsTable({ query: { unattached: "true", search: search || undefined, recordsPer: 50, pageNum: 1, sortBy: "updatedAt", sortDir: "desc" } }).then((res: any) => {
        const list = Array.isArray(res?.items) ? res.items : [];
        const key = leadName.trim().toLowerCase();
        // Likely matches on top: same or overlapping prospect name.
        list.sort((a: any, b: any) => {
          const s = (p: any) => {
            const n = String(p.prospectName ?? "").toLowerCase();
            return n === key ? 0 : n.includes(key) || key.includes(n) ? 1 : 2;
          };
          return s(a) - s(b);
        });
        setItems(list);
      });
    }, 250);
    return () => clearTimeout(t);
  }, [open, search, leadName]);

  const attach = async (id: string) => {
    setBusy(id);
    setError(null);
    const res: any = await attachProposalToLead(id, leadId);
    setBusy(null);
    if (res?.error) return setError(res.error);
    setItems((prev) => prev.filter((p) => p.id !== id));
    onAttached();
  };

  const link: React.CSSProperties = { background: "none", border: "none", color: "#185FA5", cursor: "pointer", padding: 0, fontFamily: "Poppins", fontSize: 12, fontWeight: 500 };

  return (
    <div style={{ marginBottom: 12, fontFamily: "Poppins" }}>
      <button type="button" style={link} onClick={() => setOpen((v) => !v)}>
        {open ? "Close" : "Attach an existing proposal"}
      </button>
      {open && (
        <div style={{ marginTop: 8, background: "#fff", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 8 }}>
          <div style={{ padding: "10px 12px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
            <input
              style={{ padding: "7px 9px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 6, fontSize: 13, fontFamily: "Poppins", width: "100%", boxSizing: "border-box" }}
              placeholder="Search proposals not attached to anything"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          {items.length === 0 && <div style={{ padding: "10px 12px", fontSize: 13, color: themevals.colors.dkgrey }}>No unattached proposals{search ? " match" : ""}.</div>}
          {items.map((p) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderTop: `1px solid ${themevals.colors.inputOutline}`, fontSize: 13 }}>
              <div style={{ flex: 1 }}>
                <b>{p.prospectName}</b>{p.version > 1 ? <span style={{ color: themevals.colors.dkgrey }}> v{p.version}</span> : null}
                <div style={{ fontSize: 11, color: themevals.colors.dkgrey }}>
                  {p.status}{p.sentAt ? ` \u00b7 sent ${new Date(p.sentAt).toLocaleDateString()}` : ""}{p.viewCount ? ` \u00b7 ${p.viewCount} opens` : ""}
                </div>
              </div>
              <button type="button" style={link} disabled={!!busy} onClick={() => attach(p.id)}>{busy === p.id ? "Attaching\u2026" : "Attach"}</button>
            </div>
          ))}
          {error && <div style={{ padding: "8px 12px", color: "#A60000", fontSize: 12 }}>{error}</div>}
        </div>
      )}
    </div>
  );
}
